import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { japanPrefectures, regions } from '../../mocks/japanPrefectures';
import { JapanMap } from './components/JapanMap';
import { PrefectureList } from './components/PrefectureList';
import { ArtistSearch } from './components/ArtistSearch';
import { BottomNav } from '../../components/feature/BottomNav';
import { usePrefectureCounts } from '../../hooks/usePrefectureCounts';

const REGION_COLORS: Record<string, string> = {
  'hokkaido-tohoku': '#00d9ff',
  'kanto': '#ff006e',
  'chubu': '#b537f2',
  'kinki': '#00ff41',
  'chugoku-shikoku': '#ff8c00',
  'kyushu': '#ffd600',
};

export default function DirectoryPage() {
  const navigate = useNavigate();
  const [hoveredPref, setHoveredPref] = useState<string | null>(null);
  const [selectedRegion, setSelectedRegion] = useState('all');
  const { counts, loading: countsLoading } = usePrefectureCounts();

  const totalArtists = useMemo(() => {
    if (Object.keys(counts).length > 0) {
      return Object.values(counts).reduce((sum, c) => sum + c.artistCount, 0);
    }
    return japanPrefectures.reduce((sum, p) => sum + p.artistCount, 0);
  }, [counts]);

  const totalShops = useMemo(() => {
    if (Object.keys(counts).length > 0) {
      return Object.values(counts).reduce((sum, c) => sum + c.shopCount, 0);
    }
    return japanPrefectures.reduce((sum, p) => sum + p.shopCount, 0);
  }, [counts]);

  const availableCount = japanPrefectures.filter(p => p.available).length;

  const handleSelect = (id: string) => {
    const pref = japanPrefectures.find(p => p.id === id);
    if (pref?.available) {
      navigate(`/prefecture/${id}`);
    }
  };

  // Build enriched prefectures for the map tooltip
  const enrichedPrefectures = useMemo(() => {
    return japanPrefectures.map((p) => {
      const c = counts[p.id];
      return {
        ...p,
        artistCount: c?.artistCount ?? p.artistCount,
        shopCount: c?.shopCount ?? p.shopCount,
      };
    });
  }, [counts]);

  return (
    <div className="bg-black min-h-screen pb-24">
      {/* Background noise */}
      <div
        className="fixed inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
        }}
      />

      {/* Mobile Top Bar */}
      <div className="lg:hidden fixed top-0 left-0 w-full z-50 bg-black/90 backdrop-blur-xl border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <a href="/" className="flex items-center gap-2 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/d8e1192effeb065d9828bb3d391cea6f/487e2600aaa2d450ac57cbe9e20dd89d.png"
              alt="DigDim Logo"
              className="w-7 h-7"
            />
            <span
              className="font-display text-lg text-white tracking-wider"
              style={{ textShadow: '0 0 12px rgba(0,255,65,0.3)' }}
            >
              DigDim
            </span>
          </a>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-500 tracking-widest">DIRECTORY</span>
            <div className="w-1.5 h-1.5 rounded-full bg-neon-green animate-pulse" />
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              'linear-gradient(#333 1px, transparent 1px), linear-gradient(90deg, #333 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        <div className="absolute top-0 right-0 w-64 h-64 bg-neon-green/5 blur-[100px] rounded-full" />
        <div className="absolute bottom-0 left-1/4 w-48 h-48 bg-neon-pink/5 blur-[80px] rounded-full" />

        <div className="relative z-10 container mx-auto px-4 sm:px-8 pt-20 lg:pt-24 pb-8 lg:pb-12">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-gray-500 text-sm hover:text-neon-green transition-colors duration-300 mb-6 lg:mb-8 cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            <span className="tracking-wider">TOP</span>
          </a>

          <div className="flex items-center gap-2 text-xs text-gray-600 mb-4 lg:mb-6">
            <a href="/" className="hover:text-white transition-colors cursor-pointer">HOME</a>
            <span>/</span>
            <span className="text-neon-green">DIRECTORY</span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 lg:gap-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-2 h-2 rounded-full bg-neon-green" style={{ boxShadow: '0 0 10px #00ff41' }} />
                <span className="text-gray-500 text-[10px] tracking-[0.3em] uppercase">Prefecture Directory</span>
              </div>
              <div className="flex items-baseline gap-4 mb-2">
                <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl text-white tracking-wider">
                  JAPAN
                </h1>
                <span className="text-gray-400 text-lg lg:text-2xl font-light">全国マップ</span>
              </div>
              <div
                className="w-20 h-1 bg-neon-green mb-4"
                style={{ boxShadow: '0 0 15px #00ff41' }}
              />
              <p className="text-gray-400 text-sm lg:text-base leading-relaxed max-w-lg">
                <strong className="text-white">47都道府県</strong>のアンダーグラウンドシーンを探索。
                各地域の音楽スポット、アーティスト、イベント情報をマップで確認しよう。
              </p>
            </div>

            <div className="flex gap-6 lg:gap-8">
              <div className="text-center">
                <div className="text-neon-green text-2xl lg:text-3xl font-bold">{availableCount}</div>
                <div className="text-gray-500 text-[10px] lg:text-xs tracking-wider">ACTIVE</div>
              </div>
              <div className="text-center">
                <div className="text-neon-pink text-2xl lg:text-3xl font-bold">
                  {countsLoading ? '...' : totalArtists}
                </div>
                <div className="text-gray-500 text-[10px] lg:text-xs tracking-wider">ARTISTS</div>
              </div>
              <div className="text-center">
                <div className="text-neon-blue text-2xl lg:text-3xl font-bold">
                  {countsLoading ? '...' : totalShops}
                </div>
                <div className="text-gray-500 text-[10px] lg:text-xs tracking-wider">SHOPS</div>
              </div>
            </div>
          </div>
        </div>
        <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-8 py-8 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
          {/* Map Area */}
          <div className="lg:col-span-7">
            <div
              className="bg-[#0a0a0a] rounded-2xl border border-white/5 p-4 sm:p-6 lg:p-8"
              style={{ boxShadow: '0 0 40px rgba(0, 255, 65, 0.05)' }}
            >
              <div className="flex items-center justify-between mb-4 lg:mb-6">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-neon-green" style={{ boxShadow: '0 0 8px #00ff41' }} />
                  <span className="text-gray-500 text-[10px] tracking-[0.3em] uppercase">Interactive Map</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-neon-green/10 border border-neon-green/20">
                  <i className="ri-map-2-line text-neon-green text-xs" />
                  <span className="text-neon-green text-[10px] font-bold tracking-wider">47 PREFECTURES</span>
                </div>
              </div>

              <JapanMap
                prefectures={enrichedPrefectures}
                hoveredPref={hoveredPref}
                onHover={setHoveredPref}
                onSelect={handleSelect}
              />

              {/* Region Legend */}
              <div className="mt-4 lg:mt-6 flex flex-wrap gap-3 lg:gap-4">
                {[
                  { label: '北海道・東北', color: '#00d9ff' },
                  { label: '関東', color: '#ff006e' },
                  { label: '中部', color: '#b537f2' },
                  { label: '近畿', color: '#00ff41' },
                  { label: '中国・四国', color: '#ff8c00' },
                  { label: '九州・沖縄', color: '#ffd600' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-1.5">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ background: item.color, boxShadow: `0 0 6px ${item.color}` }}
                    />
                    <span className="text-gray-500 text-[10px] tracking-wider">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 flex items-start gap-3 px-4 py-3 rounded-lg bg-white/[0.02] border border-white/5">
              <i className="ri-information-line text-gray-600 text-sm mt-0.5" />
              <p className="text-gray-600 text-xs leading-relaxed">
                マップ上の都道府県をクリックすると詳細ページに移動します。現在は奈良県のみ公開中です。
              </p>
            </div>
          </div>

          {/* Prefecture List Area */}
          <div className="lg:col-span-5">
            <div
              className="bg-[#0a0a0a] rounded-2xl border border-white/5 p-4 sm:p-6 lg:p-8 lg:sticky lg:top-8"
              style={{ boxShadow: '0 0 40px rgba(255, 0, 110, 0.03)' }}
            >
              <div className="flex items-center justify-between mb-4 lg:mb-6">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-neon-pink" style={{ boxShadow: '0 0 8px #ff006e' }} />
                    <span className="text-gray-500 text-[10px] tracking-[0.3em] uppercase">All Prefectures</span>
                  </div>
                  <h3 className="text-white font-display text-xl lg:text-2xl tracking-wider">都道府県一覧</h3>
                </div>
                <span className="text-neon-green text-sm font-bold">47</span>
              </div>

              <PrefectureList
                prefectures={japanPrefectures}
                selectedRegion={selectedRegion}
                onRegionChange={setSelectedRegion}
                hoveredPref={hoveredPref}
                onHover={setHoveredPref}
                onSelect={handleSelect}
                counts={counts}
              />
            </div>
          </div>
        </div>

        {/* Active Prefectures Highlight */}
        <div className="mt-12 lg:mt-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-2 h-2 rounded-full bg-neon-green" style={{ boxShadow: '0 0 10px #00ff41' }} />
            <span className="text-gray-500 text-[10px] tracking-[0.3em] uppercase">Now Available</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {japanPrefectures.filter(p => p.available).map((pref) => {
              const regionColor = REGION_COLORS[pref.region] || '#00ff41';
              const prefCounts = counts[pref.id];
              const artistCount = prefCounts?.artistCount ?? pref.artistCount;
              const shopCount = prefCounts?.shopCount ?? pref.shopCount;
              return (
                <a
                  key={pref.id}
                  href={`/prefecture/${pref.id}`}
                  className="relative p-6 rounded-2xl border transition-all duration-500 cursor-pointer group overflow-hidden hover:-translate-y-1"
                  style={{
                    borderColor: `${regionColor}30`,
                    boxShadow: `0 0 30px ${regionColor}10`,
                  }}
                >
                  <div
                    className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity duration-500"
                    style={{ background: `radial-gradient(circle at top right, ${regionColor}, transparent 70%)` }}
                  />
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-display text-3xl text-white tracking-wider group-hover:text-neon-green transition-colors duration-300">
                        {pref.nameEn}
                      </h4>
                      <div
                        className="w-2 h-2 rounded-full animate-pulse"
                        style={{ background: regionColor, boxShadow: `0 0 8px ${regionColor}` }}
                      />
                    </div>
                    <span className="text-gray-500 text-sm block mb-4">{pref.name} · {pref.regionEn}</span>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <i className="ri-user-line text-sm" style={{ color: regionColor }} />
                        <span className="text-white text-sm font-bold">{artistCount}</span>
                        <span className="text-gray-600 text-xs">artists</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="ri-store-2-line text-sm" style={{ color: regionColor }} />
                        <span className="text-white text-sm font-bold">{shopCount}</span>
                        <span className="text-gray-600 text-xs">shops</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="ri-calendar-event-line text-sm" style={{ color: regionColor }} />
                        <span className="text-white text-sm font-bold">{pref.eventCount}</span>
                        <span className="text-gray-600 text-xs">events</span>
                      </div>
                    </div>
                  </div>
                  <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0">
                    <i className="ri-arrow-right-line text-lg" style={{ color: regionColor }} />
                  </div>
                </a>
              );
            })}
          </div>
        </div>

        {/* Artist Search & Filter */}
        <div className="mt-12 lg:mt-16">
          <ArtistSearch />
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}
